import { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import MUIButton from "@/components/ui/Button";
import { ButtonStyleType, ColorType } from "@/types/shared/uiComponents";
import { DocumentRejectionReason } from "@/types";
import { REJECTION_REASON_OPTIONS } from "@/constants/documentRejection";
import type { RejectDocumentInput } from "../../hooks/useReviewApproval";

interface RejectDocumentDialogProps {
  open: boolean;
  /** Shown under the title so the reviewer confirms WHICH file (artboard 1j). */
  documentName?: string;
  /** True while the POST is in flight — disables both buttons. */
  isRejecting?: boolean;
  onCancel: () => void;
  onConfirm: (input: RejectDocumentInput) => void;
}

/**
 * "Reject this file?" — reason required, note optional (#7441, artboard 1j).
 *
 * The confirm button stays disabled until a reason is chosen and the footer
 * says why ("Choose a reason to reject"), so the dialog cannot produce the
 * pre-#7441 "reason not provided" row. State resets on every open: a reason
 * picked and then cancelled must not pre-select itself next time, because the
 * next reject may be for a different reason.
 */
export const RejectDocumentDialog = ({
  open,
  documentName,
  isRejecting = false,
  onCancel,
  onConfirm,
}: RejectDocumentDialogProps) => {
  const [reasonCode, setReasonCode] = useState<DocumentRejectionReason | null>(null);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (open) {
      setReasonCode(null);
      setNote("");
    }
  }, [open]);

  const hasReason = reasonCode !== null;

  return (
    <Dialog
      open={open}
      onClose={isRejecting ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      aria-labelledby="reject-document-dialog-title"
    >
      <DialogTitle id="reject-document-dialog-title" variant="h3" sx={{ pb: 0.5 }}>
        Reject this file?
        {documentName && (
          <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            {documentName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
        <FormControl component="fieldset" required>
          <FormLabel component="legend" id="reject-reason-label" sx={{ fontSize: "0.75rem", fontWeight: 600, mb: 0.5 }}>
            Reason (required)
          </FormLabel>
          <RadioGroup
            aria-labelledby="reject-reason-label"
            name="reject-reason"
            value={reasonCode ?? ""}
            onChange={(e) => setReasonCode(e.target.value as DocumentRejectionReason)}
          >
            {REJECTION_REASON_OPTIONS.map((opt) => (
              <FormControlLabel
                key={opt.code}
                value={opt.code}
                control={<Radio size="small" />}
                label={opt.label}
                disabled={isRejecting}
              />
            ))}
          </RadioGroup>
        </FormControl>

        <TextField
          label="Note for the uploader (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          multiline
          minRows={3}
          size="small"
          fullWidth
          disabled={isRejecting}
          inputProps={{ maxLength: 2000 }}
        />

        <Typography variant="body2">Nothing will be loaded; the uploader sees your reason.</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {!hasReason && (
          <Typography variant="caption" color="text.secondary" sx={{ mr: "auto" }}>
            Choose a reason to reject
          </Typography>
        )}
        <MUIButton
          variant={ButtonStyleType.Outlined}
          color={ColorType.Inherit}
          onClick={onCancel}
          disabled={isRejecting}
        >
          Cancel
        </MUIButton>
        <MUIButton
          variant={ButtonStyleType.Contained}
          color={ColorType.Error}
          disabled={!hasReason || isRejecting}
          onClick={() => {
            if (reasonCode) onConfirm({ reasonCode, note });
          }}
        >
          {isRejecting ? "Rejecting…" : "Reject file"}
        </MUIButton>
      </DialogActions>
    </Dialog>
  );
};
